/**
 * Les deux graphiques de la vue d'aigle, en SVG fait main : une courbe et des
 * barres empilées. Chaque graphique est suivi d'un tableau lisible par les
 * lecteurs d'écran (même données, mois par mois) : le dessin est masqué à
 * l'accessibilité, le tableau le remplace. Aucune dépendance de graphique.
 */
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import commun from './commun.module.css';
import styles from './graphiques.module.css';

/** Un maximum « rond » au-dessus de la valeur, pour que les quatre graduations tombent juste. */
export function maximumRond(valeur: number): number {
  if (valeur <= 0) return 1;
  const puissance = 10 ** Math.floor(Math.log10(valeur));
  for (const multiple of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (multiple * puissance >= valeur) return multiple * puissance;
  }
  return 10 * puissance;
}

const GRADUATIONS = [0, 0.25, 0.5, 0.75, 1] as const;

/** La position horizontale (en %) du centre de la colonne `i` sur `n`. */
function centreColonne(i: number, n: number): number {
  return ((i + 0.5) / n) * 100;
}

/** Une ligne de repère : les mois sous le graphique, un sur trois sur téléphone. */
function AxeMois({ mois }: { mois: string[] }): JSX.Element {
  return (
    <div
      className={styles.axeX}
      style={{ gridTemplateColumns: `repeat(${mois.length}, minmax(0, 1fr))` }}
      data-dense={mois.length > 6 ? 'true' : undefined}
      aria-hidden="true"
    >
      {mois.map((libelle, i) => (
        <span key={i} className={i === mois.length - 1 ? styles.dernier : undefined}>
          {libelle}
        </span>
      ))}
    </div>
  );
}

function Grille(): JSX.Element {
  return (
    <>
      <line className={styles.traitBase} x1="0" x2="100" y1="100" y2="100" />
      {GRADUATIONS.slice(1).map((g) => (
        <line
          key={g}
          className={styles.traitGrille}
          x1="0"
          x2="100"
          y1={100 - g * 100}
          y2={100 - g * 100}
        />
      ))}
    </>
  );
}

function AxeY({
  maximum,
  format,
}: {
  maximum: number;
  format: (valeur: number) => string;
}): JSX.Element {
  return (
    <>
      {GRADUATIONS.map((g) => (
        <span
          key={g}
          className={styles.axeY}
          style={{ top: `${100 - g * 100}%` }}
          aria-hidden="true"
        >
          {format(maximum * g)}
        </span>
      ))}
    </>
  );
}

export interface PointCourbe {
  /** Libellé court de l'axe (« sept. »). */
  court: string;
  /** Libellé complet, pour le tableau (« septembre 2026 »). */
  long: string;
  valeur: number;
}

/** Une courbe : au moins deux points. Le dernier est marqué et étiqueté. */
export function Courbe({
  points,
  legendeTableau,
  enteteLibelle,
  enteteValeur,
  formatAxe,
  formatValeur,
}: {
  points: PointCourbe[];
  legendeTableau: string;
  enteteLibelle: string;
  enteteValeur: string;
  formatAxe: (valeur: number) => string;
  formatValeur: (valeur: number) => string;
}): JSX.Element {
  const n = points.length;
  const maximum = maximumRond(Math.max(...points.map((p) => p.valeur)));
  const y = (valeur: number): number => 100 - (valeur / maximum) * 100;
  const trace = points.map((p, i) => `${centreColonne(i, n).toFixed(2)},${y(p.valeur).toFixed(2)}`);
  const dernier = points[n - 1]!;

  return (
    <figure className={styles.figure}>
      <div className={styles.graphique}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <Grille />
          <polyline className={styles.courbe} points={trace.join(' ')} />
        </svg>
        <AxeY maximum={maximum} format={formatAxe} />
        <span
          className={styles.finCourbe}
          style={{ left: `${centreColonne(n - 1, n)}%`, top: `${y(dernier.valeur)}%` }}
          aria-hidden="true"
        />
        <span
          className={styles.etiquetteFin}
          style={{ left: `${centreColonne(n - 1, n)}%`, top: `${y(dernier.valeur)}%` }}
          aria-hidden="true"
        >
          {formatValeur(dernier.valeur)}
        </span>
      </div>
      <AxeMois mois={points.map((p) => p.court)} />
      <div className={commun.srOnly}>
        <table>
          <caption>{legendeTableau}</caption>
          <thead>
            <tr>
              <th scope="col">{enteteLibelle}</th>
              <th scope="col">{enteteValeur}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.long}>
                <th scope="row">{p.long}</th>
                <td>{formatValeur(p.valeur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

export interface SerieBarres {
  cle: string;
  libelle: string;
  /** Classe de couleur du module (`prod`, `essai`…). */
  couleur: 'production' | 'essai' | 'sansContrat' | 'resiliees';
  /** Une valeur par colonne. */
  valeurs: number[];
}

export interface ColonneBarres {
  court: string;
  long: string;
}

/** Des barres empilées, une par mois, avec légende et tableau. */
export function BarresEmpilees({
  colonnes,
  series,
  legendeTableau,
  enteteLibelle,
  enteteTotal,
  formatNombre,
}: {
  colonnes: ColonneBarres[];
  series: SerieBarres[];
  legendeTableau: string;
  enteteLibelle: string;
  enteteTotal: string;
  formatNombre: (valeur: number) => string;
}): JSX.Element {
  const n = colonnes.length;
  const totaux = colonnes.map((_, i) =>
    series.reduce((somme, s) => somme + (s.valeurs[i] ?? 0), 0),
  );
  const maximum = maximumRond(Math.max(...totaux));
  const largeur = (100 / n) * 0.62;

  return (
    <figure className={styles.figure}>
      <div className={styles.graphique}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <Grille />
          {colonnes.map((_, i) => {
            let cumul = 0;
            return series.map((s) => {
              const valeur = s.valeurs[i] ?? 0;
              if (valeur <= 0) return null;
              const hauteur = (valeur / maximum) * 100;
              const haut = 100 - cumul - hauteur;
              cumul += hauteur;
              return (
                <rect
                  key={`${i}-${s.cle}`}
                  className={styles[s.couleur]}
                  x={centreColonne(i, n) - largeur / 2}
                  y={haut}
                  width={largeur}
                  height={hauteur}
                  rx="0.4"
                />
              );
            });
          })}
        </svg>
        <AxeY maximum={maximum} format={formatNombre} />
      </div>
      <AxeMois mois={colonnes.map((c) => c.court)} />
      <ul className={styles.legende}>
        {series.map((s) => (
          <li key={s.cle}>
            <i className={styles[`pastille_${s.couleur}`]} aria-hidden="true" />
            {s.libelle}
          </li>
        ))}
      </ul>
      <div className={commun.srOnly}>
        <table>
          <caption>{legendeTableau}</caption>
          <thead>
            <tr>
              <th scope="col">{enteteLibelle}</th>
              {series.map((s) => (
                <th key={s.cle} scope="col">
                  {s.libelle}
                </th>
              ))}
              <th scope="col">{enteteTotal}</th>
            </tr>
          </thead>
          <tbody>
            {colonnes.map((c, i) => (
              <tr key={c.long}>
                <th scope="row">{c.long}</th>
                {series.map((s) => (
                  <td key={s.cle}>{formatNombre(s.valeurs[i] ?? 0)}</td>
                ))}
                <td>{formatNombre(totaux[i] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

/** Le message qui remplace un graphique à moins de deux points. */
export function HistoriqueCourt(): JSX.Element {
  const { t } = useTranslation('console');
  return <p className={styles.historiqueCourt}>{t('vueDAigle.historiqueCourt')}</p>;
}
